import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowDown, ArrowUp, CheckSquare, ChevronLeft, ChevronRight, FolderKanban, GanttChartSquare,
  Link2, MessageSquare, Pencil, Play, Plus, Printer, RefreshCw, Search, Square, Ticket, Trash2, Users, X,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonText } from '@/components/Skeleton';
import { CAMINHO_LISTA, situacaoDeProjeto } from '@/features/projetos/navegacao';
import {
  obterProjeto,
  criarTarefa,
  atualizarTarefa,
  removerTarefa,
  atualizarProjeto,
  moverTarefa,
  vincularChamado,
  adicionarDependencia,
  removerDependencia,
  listarComentariosDeTarefa,
  adicionarComentarioDeTarefa,
  listarChecklist,
  adicionarItemChecklist,
  atualizarItemChecklist,
  removerItemChecklist,
  criarRisco,
  atualizarRisco,
  alocarRecurso,
  desalocarRecurso,
  buscarMembros,
  listarAtividadeDeTarefa,
  obterBurndown,
  listarColaboradores,
  adicionarColaborador,
  removerColaborador,
  listarMensagens,
  criarMensagem,
  responderMensagem,
  obterSaude,
  listarSecoesDoProjeto,
  criarSecao,
  renomearSecao,
  excluirSecao,
  listarHoras,
  registrarHoras,
  excluirRegistroHoras,
  listarCampos,
  criarCampo,
  atualizarCampo,
  excluirCampo,
  type SaudeDoProjeto,
  type TarefaDeProjeto,
  type RiscoDeProjeto,
  type RecursoDeProjeto,
  type ChecklistItem,
  type MembroParaMencao,
  type AtividadeDeTarefa,
  type ColaboradorDeTarefa,
  type MensagemDeProjeto,
  type ListaDeHoras,
  type RegistroDeHoras,
  type CampoPersonalizado,
} from '@/features/projetos/projetos.api';
import { useAuthStore } from '@/features/auth/auth.store';
import { listarEquipes, listarMembros } from '@/features/admin/admin.api';
import { listarChamados } from '@/features/tickets/tickets.api';
import { GanttChart } from '@/features/projetos/GanttChart';
import { useScheduleSse } from '@/features/projetos/useScheduleSse';
import { SeletorDePessoa } from '@/features/tickets/pessoas';
import type { PersonRef } from '@central/contracts';
import { cn } from '@/lib/cn';

type Aba = 'wbs' | 'board' | 'calendario' | 'burndown' | 'gantt' | 'marcos' | 'riscos' | 'recursos' | 'mensagens' | 'saude';

const CHAVE_PROJETO = (id: string) => ['projetos', 'detalhe', id] as const;

const CONSTRAINT_PT: Record<string, string> = {
  ASAP: 'O mais cedo possível',
  ALAP: 'O mais tarde possível',
  SNET: 'Iniciar não antes de',
  SNLT: 'Iniciar não depois de',
  FNET: 'Terminar não antes de',
  FNLT: 'Terminar não depois de',
  MSO: 'Deve iniciar em',
  MFO: 'Deve terminar em',
};

const DEP_PT: Record<string, string> = {
  FS: 'FS — Término→Início',
  SS: 'SS — Início→Início',
  FF: 'FF — Término→Término',
  SF: 'SF — Início→Término',
};

const SEV_COR: Record<string, string> = {
  LOW: 'text-success',
  MEDIUM: 'text-warning',
  HIGH: 'text-warning',
  CRITICAL: 'text-destructive',
};
const SEV_PT: Record<string, string> = { LOW: 'Baixo', MEDIUM: 'Médio', HIGH: 'Alto', CRITICAL: 'Crítico' };
const STATUS_RISCO_PT: Record<string, string> = {
  OPEN: 'Aberto', MITIGATED: 'Mitigado', ACCEPTED: 'Aceito', CLOSED: 'Fechado',
};

function epochToDateInput(ep: number | null): string {
  if (!ep) return '';
  return new Date(ep * 60 * 1000).toISOString().slice(0, 10);
}
function dateInputToEpoch(s: string): number | null {
  if (!s) return null;
  return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 60000);
}
function fmtData(ep: number | null): string {
  if (!ep) return '—';
  return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// ── Campo estilizado ──────────────────────────────────────────────────────────

const INPUT_CLS = 'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow';
const SELECT_CLS = INPUT_CLS;

// ── Modal genérico ────────────────────────────────────────────────────────────

function Modal({ titulo, onFechar, children }: { titulo: string; onFechar: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold">{titulo}</h2>
          <button onClick={onFechar} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ── Modal editar tarefa ───────────────────────────────────────────────────────

function ModalEditarTarefa({
  projetoId, tarefa, tarefas, onFechar, onAtualizar,
}: {
  projetoId: string;
  tarefa: TarefaDeProjeto;
  tarefas: TarefaDeProjeto[];
  onFechar: () => void;
  onAtualizar: () => void;
}) {
  const [nome, setNome] = useState(tarefa.name);
  const [descricao, setDescricao] = useState(tarefa.description ?? '');
  const [duracaoHoras, setDuracaoHoras] = useState(String(Math.round(tarefa.durationMinutes / 60)));
  const [progresso, setProgresso] = useState(String(tarefa.progress));
  const [constraintType, setConstraintType] = useState(tarefa.constraintType);
  const [constraintDate, setConstraintDate] = useState(epochToDateInput(tarefa.constraintDate));
  const [deadline, setDeadline] = useState(epochToDateInput(tarefa.deadline));
  const [teamId, setTeamId] = useState(tarefa.teamId ?? '');
  const [assigneeId, setAssigneeId] = useState(tarefa.assigneeId ?? '');
  const [abaLocal, setAbaLocal] = useState<'geral' | 'pessoas' | 'checklist' | 'comentarios' | 'historico' | 'deps' | 'horas' | 'campos'>('geral');
  const [recurrence, setRecurrence] = useState(tarefa.recurrence ?? 'NONE');
  const [recurrenceInterval, setRecurrenceInterval] = useState(tarefa.recurrenceInterval ?? 1);
  const [novoComentario, setNovoComentario] = useState('');
  const [novoItemChecklist, setNovoItemChecklist] = useState('');

  const needsConstraintDate = !['ASAP', 'ALAP'].includes(constraintType);
  const esMilestone = tarefa.isMilestone;
  const progressoNum = Math.min(100, Math.max(0, Number(progresso)));

  const { data: equipes = [] } = useQuery({
    queryKey: ['admin', 'equipes'],
    queryFn: async () => { const r = await listarEquipes(); return r.items; },
  });

  const { data: membros = [] } = useQuery({
    queryKey: ['admin', 'equipe-membros', teamId],
    queryFn: async () => { if (!teamId) return []; const r = await listarMembros(teamId); return r.items; },
    enabled: !!teamId,
  });

  const { data: comentarios = [], refetch: refetchComentarios } = useQuery({
    queryKey: ['task-comments', tarefa.id],
    queryFn: () => listarComentariosDeTarefa(projetoId, tarefa.id),
    enabled: abaLocal === 'comentarios',
  });

  const { data: checklist = [], refetch: refetchChecklist } = useQuery({
    queryKey: ['task-checklist', tarefa.id],
    queryFn: () => listarChecklist(projetoId, tarefa.id),
    enabled: abaLocal === 'checklist',
  });

  const { data: atividades = [] } = useQuery({
    queryKey: ['tarefa', 'atividade', tarefa.id],
    queryFn: () => listarAtividadeDeTarefa(projetoId, tarefa.id),
    enabled: abaLocal === 'historico',
  });

  const qc = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  const horasQuery = useQuery({
    queryKey: ['projetos', 'horas', projetoId, tarefa.id],
    queryFn: () => listarHoras(projetoId, tarefa.id),
    enabled: abaLocal === 'horas',
  });

  const registrarHorasMut = useMutation({
    mutationFn: ({ minutes, description }: { minutes: number; description?: string }) =>
      registrarHoras(projetoId, tarefa.id, minutes, description),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'horas', projetoId, tarefa.id] }),
  });

  const excluirHoraMut = useMutation({
    mutationFn: (logId: string) => excluirRegistroHoras(projetoId, tarefa.id, logId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'horas', projetoId, tarefa.id] }),
  });

  const camposQuery = useQuery({
    queryKey: ['projetos', 'campos', projetoId, tarefa.id],
    queryFn: () => listarCampos(projetoId, tarefa.id),
    enabled: abaLocal === 'campos',
  });

  const criarCampoMut = useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) =>
      criarCampo(projetoId, tarefa.id, name, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'campos', projetoId, tarefa.id] }),
  });

  const atualizarCampoMut = useMutation({
    mutationFn: ({ fieldId, value }: { fieldId: string; value: string }) =>
      atualizarCampo(projetoId, tarefa.id, fieldId, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'campos', projetoId, tarefa.id] }),
  });

  const excluirCampoMut = useMutation({
    mutationFn: (fieldId: string) => excluirCampo(projetoId, tarefa.id, fieldId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'campos', projetoId, tarefa.id] }),
  });

  const adicionarItem = useMutation({
    mutationFn: () => adicionarItemChecklist(projetoId, tarefa.id, novoItemChecklist.trim()),
    onSuccess: () => { setNovoItemChecklist(''); void refetchChecklist(); },
  });

  const toggleItem = useMutation({
    mutationFn: ({ itemId, done }: { itemId: string; done: boolean }) =>
      atualizarItemChecklist(projetoId, tarefa.id, itemId, { done }),
    onSuccess: () => void refetchChecklist(),
  });

  const removerItem = useMutation({
    mutationFn: (itemId: string) => removerItemChecklist(projetoId, tarefa.id, itemId),
    onSuccess: () => void refetchChecklist(),
  });

  const atualizar = useMutation({
    mutationFn: (overrides?: { progress?: number }) =>
      atualizarTarefa(projetoId, tarefa.id, {
        name: nome.trim(),
        description: descricao.trim() || null,
        ...(esMilestone ? {} : { durationMinutes: Math.max(1, Math.round(Number(duracaoHoras) * 60)) }),
        progress: overrides?.progress ?? progressoNum,
        assigneeId: assigneeId.trim() || null,
        teamId: teamId.trim() || null,
        constraintType,
        constraintDate: needsConstraintDate ? dateInputToEpoch(constraintDate) : null,
        deadline: dateInputToEpoch(deadline),
        ...(recurrence !== 'NONE' ? { recurrence, recurrenceInterval: Number(recurrenceInterval) } : { recurrence: 'NONE' }),
      }),
    onSuccess: () => { onAtualizar(); onFechar(); },
  });

  const remover = useMutation({
    mutationFn: () => removerTarefa(projetoId, tarefa.id),
    onSuccess: () => { onAtualizar(); onFechar(); },
  });

  const enviarComentario = useMutation({
    mutationFn: () => adicionarComentarioDeTarefa(projetoId, tarefa.id, novoComentario.trim()),
    onSuccess: () => { setNovoComentario(''); void refetchComentarios(); },
  });

  // Dependências
  const [predSelecionado, setPredSelecionado] = useState('');
  const [kindSelecionado, setKindSelecionado] = useState<'FS' | 'SS' | 'FF' | 'SF'>('FS');
  const [lag, setLag] = useState('0');

  const adicionarDep = useMutation({
    mutationFn: () =>
      adicionarDependencia(projetoId, predSelecionado, {
        successorId: tarefa.id,
        kind: kindSelecionado,
        lagMinutes: Number(lag) * 60,
      }),
    onSuccess: () => { onAtualizar(); setPredSelecionado(''); setLag('0'); },
  });

  const removerDep = useMutation({
    mutationFn: (depId: string) => removerDependencia(projetoId, depId),
    onSuccess: () => onAtualizar(),
  });

  const outrasTarefas = tarefas.filter((t) => t.id !== tarefa.id);
  const predIds = new Set(tarefa.predecessors.map((d) => d.predecessorId));

  const statusLabel = progressoNum === 0 ? 'A fazer' : progressoNum === 100 ? 'Concluída' : 'Em andamento';
  const statusCls = progressoNum === 0
    ? 'bg-muted text-muted-foreground'
    : progressoNum === 100
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400';

  const doneCount = checklist.filter((i: ChecklistItem) => i.done).length;
  const ABAS = [
    { id: 'geral', label: 'Geral' },
    { id: 'pessoas', label: 'Equipe' },
    { id: 'checklist', label: checklist.length > 0 ? `Checklist (${doneCount}/${checklist.length})` : 'Checklist' },
    { id: 'comentarios', label: 'Comentários' },
    { id: 'historico', label: 'Histórico' },
    { id: 'deps', label: `Dependências (${tarefa.predecessors.length})` },
    { id: 'horas', label: 'Horas' },
    { id: 'campos', label: 'Campos' },
  ] as const;

  return (
    <Modal titulo={`Tarefa ${tarefa.outlineNumber} — ${tarefa.name}`} onFechar={onFechar}>
      {/* Sub-abas */}
      <div className="mb-4 flex gap-0 border-b border-border -mx-5 px-5 overflow-x-auto">
        {ABAS.map((a) => (
          <button
            key={a.id}
            onClick={() => setAbaLocal(a.id)}
            className={cn(
              'whitespace-nowrap pb-2 px-3 text-sm font-medium transition-colors',
              abaLocal === a.id ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* ── Geral ── */}
      {abaLocal === 'geral' && (
        <div className="flex flex-col gap-3">
          {/* Status + progresso */}
          {!esMilestone && (
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', statusCls)}>{statusLabel}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{progressoNum}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={cn('h-full rounded-full transition-all', progressoNum === 100 ? 'bg-green-500' : 'bg-primary')}
                  style={{ width: `${progressoNum}%` }}
                />
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                {progressoNum === 0 && (
                  <button
                    onClick={() => { setProgresso('1'); }}
                    className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    <Play className="h-3 w-3" /> Iniciar tarefa
                  </button>
                )}
                {progressoNum > 0 && progressoNum < 100 && (
                  <button
                    onClick={() => { setProgresso('100'); }}
                    className="flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                  >
                    Concluir
                  </button>
                )}
                <div className="flex items-center gap-1 ml-auto">
                  <label className="text-xs text-muted-foreground">Progresso:</label>
                  <input
                    className="w-16 h-7 rounded border border-input/80 bg-muted/40 px-2 text-xs text-center"
                    type="number" min="0" max="100" value={progresso}
                    onChange={(e) => setProgresso(e.target.value)}
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Nome</label>
            <input className={INPUT_CLS} value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Descrição</label>
            <textarea className={INPUT_CLS} rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Opcional" />
          </div>
          {esMilestone && (
            <p className="rounded-md bg-primary/10 px-3 py-2 text-xs text-primary">
              ◆ Marco — duração zero. A data é calculada pelo cronograma conforme as dependências.
            </p>
          )}
          {!esMilestone && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Duração (horas)</label>
              <input className={INPUT_CLS} type="number" min="0.5" step="0.5" value={duracaoHoras} onChange={(e) => setDuracaoHoras(e.target.value)} />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Restrição</label>
            <select className={SELECT_CLS} value={constraintType} onChange={(e) => setConstraintType(e.target.value as typeof constraintType)}>
              {Object.entries(CONSTRAINT_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {needsConstraintDate && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Data da restrição</label>
              <input className={INPUT_CLS} type="date" value={constraintDate} onChange={(e) => setConstraintDate(e.target.value)} />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Prazo limite (deadline)</label>
            <input className={INPUT_CLS} type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>

          {/* Recorrência */}
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-xs text-muted-foreground">Recorrência</span>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs focus-visible:outline-none"
            >
              <option value="NONE">Sem recorrência</option>
              <option value="DAILY">Diária</option>
              <option value="WEEKLY">Semanal</option>
              <option value="MONTHLY">Mensal</option>
            </select>
            {recurrence !== 'NONE' && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">a cada</span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={recurrenceInterval}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!val || val < 1) return;
                    setRecurrenceInterval(val);
                  }}
                  className="h-8 w-14 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs tabular-nums text-center focus-visible:outline-none"
                />
                <span className="text-xs text-muted-foreground">
                  {recurrence === 'DAILY' ? 'dia(s)' : recurrence === 'WEEKLY' ? 'semana(s)' : 'mês(es)'}
                </span>
              </div>
            )}
          </div>

          {remover.isError && <p className="text-xs text-destructive">Erro ao remover — verifique se há subtarefas.</p>}

          <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
            <button
              onClick={() => { if (confirm(`Remover tarefa "${tarefa.name}"?`)) remover.mutate(); }}
              disabled={remover.isPending}
              className="flex items-center gap-1.5 text-xs text-destructive hover:underline disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remover tarefa
            </button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onFechar}>Cancelar</Button>
              <Button size="sm" disabled={!nome.trim() || atualizar.isPending} loading={atualizar.isPending} onClick={() => atualizar.mutate()}>Salvar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Equipe / Pessoas ── */}
      {abaLocal === 'pessoas' && (
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Equipe</label>
            <select
              className={SELECT_CLS}
              value={teamId}
              onChange={(e) => { setTeamId(e.target.value); setAssigneeId(''); }}
            >
              <option value="">Sem equipe</option>
              {equipes.map((eq) => <option key={eq.id} value={eq.id}>{eq.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Responsável</label>
            <select
              className={SELECT_CLS}
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              disabled={!teamId}
            >
              <option value="">Sem responsável</option>
              {membros.map((m: { userId: string; name: string }) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
            </select>
            {!teamId && <p className="mt-1 text-xs text-muted-foreground">Selecione a equipe primeiro.</p>}
          </div>
          {(tarefa.team || tarefa.assignee) && (
            <div className="rounded-md border border-border bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
              Atual: <span className="font-medium text-foreground">{tarefa.team?.name ?? '—'}</span>
              {tarefa.assignee && <> · <span className="font-medium text-foreground">{tarefa.assignee.displayName ?? tarefa.assignee.name}</span></>}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" size="sm" onClick={onFechar}>Cancelar</Button>
            <Button size="sm" disabled={atualizar.isPending} loading={atualizar.isPending} onClick={() => atualizar.mutate()}>Salvar</Button>
          </div>
        </div>
      )}

      {/* ── Comentários ── */}
      {abaLocal === 'comentarios' && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
            {comentarios.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nenhum comentário ainda.</p>
            ) : (
              comentarios.map((c) => (
                <div key={c.id} className="rounded-lg border border-border bg-muted/10 px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-foreground">
                      {c.author ? (c.author.displayName ?? c.author.name) : 'Sistema'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(c.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap"><CorpoComMencoes texto={c.body} /></p>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-border pt-3">
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Novo comentário <span className="text-muted-foreground/50">— use @Nome para mencionar alguém</span>
            </label>
            <MentionTextarea
              value={novoComentario}
              onChange={setNovoComentario}
              rows={3}
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                disabled={!novoComentario.trim() || enviarComentario.isPending}
                loading={enviarComentario.isPending}
                onClick={() => enviarComentario.mutate()}
                icon={<MessageSquare className="h-3.5 w-3.5" />}
              >
                Enviar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Checklist ── */}
      {abaLocal === 'checklist' && (
        <div className="flex flex-col gap-3">
          {checklist.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.round((doneCount / checklist.length) * 100)}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                {doneCount}/{checklist.length}
              </span>
            </div>
          )}
          <ul className="flex flex-col gap-1">
            {checklist.map((item: ChecklistItem) => (
              <li key={item.id} className="flex items-center gap-2 group rounded-md px-1 py-0.5 hover:bg-muted/20">
                <button
                  onClick={() => toggleItem.mutate({ itemId: item.id, done: !item.done })}
                  disabled={toggleItem.isPending}
                  className="shrink-0 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                  aria-label={item.done ? 'Desmarcar' : 'Marcar como feito'}
                >
                  {item.done
                    ? <CheckSquare className="h-4 w-4 text-primary" />
                    : <Square className="h-4 w-4" />}
                </button>
                <span className={cn('flex-1 text-sm', item.done && 'line-through text-muted-foreground')}>
                  {item.body}
                </span>
                <button
                  onClick={() => removerItem.mutate(item.id)}
                  disabled={removerItem.isPending}
                  className="opacity-0 group-hover:opacity-100 rounded p-0.5 text-muted-foreground hover:text-destructive transition-all disabled:opacity-40"
                  aria-label="Remover item"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 border-t border-border pt-3">
            <input
              className={cn(INPUT_CLS, 'flex-1')}
              value={novoItemChecklist}
              onChange={(e) => setNovoItemChecklist(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && novoItemChecklist.trim()) adicionarItem.mutate(); }}
              placeholder="Adicionar item…"
            />
            <Button
              size="sm"
              disabled={!novoItemChecklist.trim() || adicionarItem.isPending}
              loading={adicionarItem.isPending}
              onClick={() => adicionarItem.mutate()}
            >
              Adicionar
            </Button>
          </div>
        </div>
      )}

      {/* ── Histórico ── */}
      {abaLocal === 'historico' && (
        <div className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
          {atividades.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Sem histórico ainda.</p>
          ) : (
            atividades.map((a: AtividadeDeTarefa) => (
              <div key={a.id} className="flex items-start gap-2.5 text-xs">
                <div className="mt-0.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0 mt-1.5" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{a.user ? (a.user.displayName ?? a.user.name) : 'Sistema'}</span>
                  {' '}<span className="text-muted-foreground"><AtividadeDescricao action={a.action} field={a.field} oldValue={a.oldValue} newValue={a.newValue} /></span>
                  <span className="block text-[10px] text-muted-foreground/60 mt-0.5">
                    {new Date(a.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Dependências ── */}
      {abaLocal === 'deps' && (
        <div className="flex flex-col gap-4">
          {tarefa.predecessors.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma predecessora definida.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {tarefa.predecessors.map((dep) => {
                const pred = tarefas.find((t) => t.id === dep.predecessorId);
                return (
                  <li key={dep.id} className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
                    <span>
                      <span className="font-mono text-xs text-muted-foreground mr-1">{pred?.outlineNumber ?? '?'}</span>
                      <span className="font-medium">{pred?.name ?? dep.predecessorId.slice(0, 8)}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {DEP_PT[dep.kind] ?? dep.kind}
                        {dep.lagMinutes > 0 && ` +${Math.round(dep.lagMinutes / 60)}h`}
                      </span>
                    </span>
                    <button
                      onClick={() => removerDep.mutate(dep.id)}
                      disabled={removerDep.isPending}
                      className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-40"
                      aria-label="Remover dependência"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-t border-border pt-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Adicionar predecessora</p>
            <div className="flex flex-col gap-2">
              <select className={SELECT_CLS} value={predSelecionado} onChange={(e) => setPredSelecionado(e.target.value)}>
                <option value="">Selecione a tarefa predecessora…</option>
                {outrasTarefas.filter((t) => !predIds.has(t.id)).map((t) => (
                  <option key={t.id} value={t.id}>{t.outlineNumber} — {t.name}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <select className={SELECT_CLS} value={kindSelecionado} onChange={(e) => setKindSelecionado(e.target.value as typeof kindSelecionado)}>
                  {Object.entries(DEP_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input className={INPUT_CLS} type="number" min="0" step="1" value={lag} onChange={(e) => setLag(e.target.value)} placeholder="Lag (horas)" />
              </div>
              <Button size="sm" disabled={!predSelecionado || adicionarDep.isPending} loading={adicionarDep.isPending} onClick={() => adicionarDep.mutate()}>
                Adicionar dependência
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Após salvar dependências o cronograma é recomputado automaticamente.
          </p>
        </div>
      )}

      {/* ── Horas ── */}
      {abaLocal === 'horas' && (
        <HorasView
          data={horasQuery.data}
          isLoading={horasQuery.isPending}
          onRegistrar={(minutes, desc) => registrarHorasMut.mutate({ minutes, description: desc })}
          onExcluir={(id) => excluirHoraMut.mutate(id)}
          currentUserId={currentUserId}
        />
      )}

      {/* ── Campos personalizados ── */}
      {abaLocal === 'campos' && (
        <CamposView
          data={camposQuery.data}
          isLoading={camposQuery.isPending}
          onCriar={(name, value) => criarCampoMut.mutate({ name, value })}
          onAtualizar={(id, value) => atualizarCampoMut.mutate({ fieldId: id, value })}
          onExcluir={(id) => excluirCampoMut.mutate(id)}
        />
      )}

      {/* Colaboradores — sempre visível */}
      <div className="border-t border-border pt-3 mt-2">
        <ColaboradoresSection projetoId={projetoId} tarefaId={tarefa.id} />
      </div>
    </Modal>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export function ProjetoDetalhePage({ projetoId }: { projetoId: string }) {
  const qc = useQueryClient();
  const [aba, setAba] = useState<Aba>('wbs');
  const [tarefaEditandoCalendario, setTarefaEditandoCalendario] = useState<TarefaDeProjeto | null>(null);

  const CHAVE = CHAVE_PROJETO(projetoId);

  const consulta = useQuery({
    queryKey: CHAVE,
    queryFn: () => obterProjeto(projetoId),
  });

  useScheduleSse(projetoId, CHAVE);

  const atualizarStatusMutation = useMutation({
    mutationFn: (status: string) => atualizarProjeto(projetoId, { status: status as any }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: CHAVE_PROJETO(projetoId) }),
  });

  const invalidar = () => void qc.invalidateQueries({ queryKey: CHAVE_PROJETO(projetoId) });

  const burndown = useQuery({
    queryKey: ['projetos', 'burndown', projetoId],
    queryFn: () => obterBurndown(projetoId),
    enabled: aba === 'burndown',
  });

  const mensagensQuery = useQuery({
    queryKey: ['projetos', 'mensagens', projetoId],
    queryFn: () => listarMensagens(projetoId),
    enabled: aba === 'mensagens',
  });

  const saude = useQuery({
    queryKey: ['projetos', 'saude', projetoId],
    queryFn: () => obterSaude(projetoId),
    enabled: aba === 'saude',
    refetchInterval: 60_000,
  });

  const mudarProgresso = useMutation({
    mutationFn: ({ tarefaId, progress, boardSectionId }: { tarefaId: string; progress?: number; boardSectionId?: string }) =>
      atualizarTarefa(projetoId, tarefaId, {
        ...(progress !== undefined ? { progress } : {}),
        ...(boardSectionId !== undefined ? { boardSectionId } : {}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: CHAVE_PROJETO(projetoId) }),
  });

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonText className="h-6 w-48" />
        <SkeletonText className="h-4 w-96" />
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="p-6">
        <ErrorState error={consulta.error} onRetry={() => void consulta.refetch()} />
      </div>
    );
  }

  const projeto = consulta.data!;
  const situacao = situacaoDeProjeto(projeto.status);

  return (
    <div className="flex flex-col gap-4 p-4">
      <Link
        to={CAMINHO_LISTA}
        className="flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Projetos
      </Link>

      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <FolderKanban className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{projeto.number}</span>
              <span className={cn('text-xs font-medium', situacao.cor)}>{situacao.rotulo}</span>
            </div>
            <h1 className="text-lg font-semibold">{projeto.name}</h1>
            {projeto.description && (
              <p className="mt-0.5 text-sm text-muted-foreground">{projeto.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 rounded-lg border border-input/80 bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors print:hidden"
            title="Exportar como PDF"
          >
            <Printer className="h-3.5 w-3.5" />
            PDF
          </button>
          {projeto.status === 'DRAFT' && (
            <Button size="sm" variant="secondary" onClick={() => atualizarStatusMutation.mutate('PLANNING')} disabled={atualizarStatusMutation.isPending}>
              Iniciar planejamento
            </Button>
          )}
          {projeto.status === 'PLANNING' && (
            <Button size="sm" variant="primary" onClick={() => atualizarStatusMutation.mutate('ACTIVE')} disabled={atualizarStatusMutation.isPending}>
              Ativar projeto
            </Button>
          )}
          {projeto.status === 'ACTIVE' && (
            <Button size="sm" variant="secondary" onClick={() => atualizarStatusMutation.mutate('COMPLETED')} disabled={atualizarStatusMutation.isPending}>
              Marcar concluído
            </Button>
          )}
        </div>
      </div>

      {/* Abas */}
      <div className="border-b border-border">
        <nav className="-mb-px flex gap-0">
          {(
            [
              { id: 'wbs', label: `Lista (${projeto.tasks.length})` },
              { id: 'board', label: 'Board' },
              { id: 'calendario', label: 'Calendário' },
              { id: 'burndown', label: 'Burndown' },
              { id: 'gantt', label: 'Gantt' },
              { id: 'marcos', label: `Marcos (${projeto.tasks.filter((t) => t.isMilestone).length})` },
              { id: 'riscos', label: `Riscos (${projeto.risks.length})` },
              { id: 'recursos', label: `Recursos (${projeto.resources.length})` },
              { id: 'mensagens', label: 'Mensagens' },
              { id: 'saude', label: 'Saúde' },
            ] as Array<{ id: Aba; label: string }>
          ).map((a) => (
            <button
              key={a.id}
              onClick={() => setAba(a.id)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium transition-colors',
                aba === a.id ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {a.label}
            </button>
          ))}
        </nav>
      </div>

      {aba === 'wbs' && (
        <WbsView projetoId={projetoId} tarefas={projeto.tasks} onAtualizar={invalidar} />
      )}

      {aba === 'board' && (
        <BoardView
          projetoId={projetoId}
          tarefas={projeto.tasks}
          onMoverTarefa={(tarefaId, secaoId, isDone) =>
            mudarProgresso.mutate({ tarefaId, progress: isDone ? 100 : undefined, boardSectionId: secaoId })
          }
        />
      )}

      {aba === 'calendario' && (
        <CalendarioView tasks={projeto.tasks} onAbrirTarefa={(t) => setTarefaEditandoCalendario(t)} />
      )}

      {aba === 'burndown' && (
        <BurndownView data={burndown.data} isLoading={burndown.isLoading} />
      )}

      {aba === 'gantt' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <GanttChartSquare className="h-4 w-4 text-primary" />
            <span>
              {projeto.tasks.filter((t) => t.startAt != null).length} tarefa(s) com datas calculadas.
              O gráfico atualiza automaticamente quando o cronograma é recomputado.
            </span>
          </div>
          <GanttChart tarefas={projeto.tasks} />
        </div>
      )}

      {aba === 'marcos' && <MarcosView projetoId={projetoId} tarefas={projeto.tasks} onAtualizar={invalidar} />}
      {aba === 'riscos' && <RiscosView projetoId={projetoId} riscos={projeto.risks} onAtualizar={invalidar} />}
      {aba === 'recursos' && <RecursosView projetoId={projetoId} recursos={projeto.resources} onAtualizar={invalidar} />}

      {aba === 'mensagens' && (
        <MensagensView projetoId={projetoId} data={mensagensQuery.data} isLoading={mensagensQuery.isLoading} />
      )}

      {aba === 'saude' && <SaudeView data={saude.data} isLoading={saude.isLoading} />}

      <style>{`@media print { nav, aside, [data-sidebar] { display: none !important; } }`}</style>

      {tarefaEditandoCalendario && (
        <ModalEditarTarefa
          projetoId={projetoId}
          tarefa={tarefaEditandoCalendario}
          tarefas={projeto.tasks}
          onFechar={() => setTarefaEditandoCalendario(null)}
          onAtualizar={() => { invalidar(); setTarefaEditandoCalendario(null); }}
        />
      )}
    </div>
  );
}

// ── WBS ───────────────────────────────────────────────────────────────────────

function WbsView({
  projetoId, tarefas, onAtualizar,
}: {
  projetoId: string;
  tarefas: TarefaDeProjeto[];
  onAtualizar: () => void;
}) {
  const [adicionando, setAdicionando] = useState(false);
  const [nomeTarefa, setNomeTarefa] = useState('');
  const [duracaoH, setDuracaoH] = useState('8');
  const [tarefaEditando, setTarefaEditando] = useState<TarefaDeProjeto | null>(null);
  const [vinculandoId, setVinculandoId] = useState<string | null>(null);
  const [buscaChamado, setBuscaChamado] = useState('');
  const [chamadoSelecionadoId, setChamadoSelecionadoId] = useState<string | null>(null);
  const [adicionandoSubtarefaPai, setAdicionandoSubtarefaPai] = useState<string | null>(null);
  const [nomeSubtarefa, setNomeSubtarefa] = useState('');
  const [duracaoSubtarefaH, setDuracaoSubtarefaH] = useState('8');
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const dragState = useRef<{ tarefaId: string; parentId: string | null; idx: number } | null>(null);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());

  const { data: membros = [] } = useQuery({
    queryKey: ['projetos', 'membros'],
    queryFn: () => buscarMembros(''),
  });

  const criar = useMutation({
    mutationFn: () => criarTarefa(projetoId, { name: nomeTarefa.trim(), durationMinutes: Math.max(1, Math.round(Number(duracaoH) * 60)) }),
    onSuccess: () => { setNomeTarefa(''); setDuracaoH('8'); setAdicionando(false); onAtualizar(); },
  });

  const criarSubtarefa = useMutation({
    mutationFn: () => criarTarefa(projetoId, {
      name: nomeSubtarefa.trim(),
      parentId: adicionandoSubtarefaPai!,
      durationMinutes: Math.max(1, Math.round(Number(duracaoSubtarefaH) * 60)),
    }),
    onSuccess: () => { setAdicionandoSubtarefaPai(null); setNomeSubtarefa(''); setDuracaoSubtarefaH('8'); onAtualizar(); },
  });

  const mover = useMutation({
    mutationFn: ({ tarefaId, position, parentId }: { tarefaId: string; position: number; parentId?: string | null }) =>
      moverTarefa(projetoId, tarefaId, { parentId, position }),
    onSuccess: onAtualizar,
  });

  const vincular = useMutation({
    mutationFn: ({ tarefaId, ticketId }: { tarefaId: string; ticketId: string | null }) =>
      vincularChamado(tarefaId, ticketId),
    onSuccess: () => { setVinculandoId(null); setBuscaChamado(''); setChamadoSelecionadoId(null); onAtualizar(); },
  });

  const { data: chamadosSugeridos = [] } = useQuery({
    queryKey: ['tickets-sugestao', buscaChamado],
    queryFn: async () => { const r = await listarChamados({ q: buscaChamado, limit: 8 }); return r.items; },
    enabled: !!buscaChamado && buscaChamado.length >= 2 && vinculandoId !== null,
  });

  function handleMover(tarefa: TarefaDeProjeto, direcao: 'cima' | 'baixo') {
    const irmaos = tarefas
      .filter((t) => t.parentId === tarefa.parentId && t.depth === tarefa.depth)
      .sort((a, b) => a.outlineNumber.localeCompare(b.outlineNumber, 'pt', { numeric: true }));
    const idx = irmaos.findIndex((t) => t.id === tarefa.id);
    const nova = direcao === 'cima' ? idx - 1 : idx + 1;
    if (nova < 0 || nova >= irmaos.length) return;
    mover.mutate({ tarefaId: tarefa.id, position: nova });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {tarefas.length === 0 ? 'Nenhuma tarefa ainda.' : `${tarefas.length} tarefa(s)`}
        </p>
        {!adicionando && (
          <Button size="sm" variant="secondary" onClick={() => setAdicionando(true)} icon={<Plus className="h-3.5 w-3.5" />}>
            Adicionar tarefa
          </Button>
        )}
      </div>

      {adicionando && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/20 p-3">
          <input
            autoFocus
            value={nomeTarefa}
            onChange={(e) => setNomeTarefa(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') criar.mutate(); if (e.key === 'Escape') setAdicionando(false); }}
            placeholder="Nome da tarefa"
            className="flex-1 min-w-40 rounded-lg border border-input/80 bg-muted/40 px-2 py-1.5 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
          />
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-muted-foreground whitespace-nowrap">Dur. (h):</label>
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={duracaoH}
              onChange={(e) => setDuracaoH(e.target.value)}
              className="w-20 rounded-lg border border-input/80 bg-muted/40 px-2 py-1.5 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
            />
          </div>
          <Button size="sm" variant="primary" onClick={() => criar.mutate()} disabled={!nomeTarefa.trim() || criar.isPending}>
            {criar.isPending ? '…' : 'Adicionar'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdicionando(false); setNomeTarefa(''); setDuracaoH('8'); }}>
            Cancelar
          </Button>
        </div>
      )}

      {tarefas.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-14">#</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Nome</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-28">Status</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-28">Responsável</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-24">Início</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-24">Término</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-28">Predecessoras</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground w-28">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tarefas.map((t) => {
                const nowMin = Math.floor(Date.now() / 60_000);
                const delayed = !t.isMilestone && t.progress < 100 && t.finishAt !== null && t.finishAt < nowMin;
                const irmaos = tarefas
                  .filter((x) => x.parentId === t.parentId && x.depth === t.depth)
                  .sort((a, b) => a.outlineNumber.localeCompare(b.outlineNumber, 'pt', { numeric: true }));
                const idx = irmaos.findIndex((x) => x.id === t.id);

                return (
                  <Fragment key={t.id}>
                  <tr
                    draggable="true"
                    onDragStart={() => {
                      dragState.current = { tarefaId: t.id, parentId: t.parentId, idx };
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (dragState.current && dragState.current.parentId === t.parentId) {
                        setDropTargetId(t.id);
                      }
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setDropTargetId(null);
                      }
                    }}
                    onDrop={() => {
                      if (!dragState.current || dragState.current.tarefaId === t.id) {
                        setDropTargetId(null);
                        return;
                      }
                      if (dragState.current.parentId !== t.parentId) {
                        setDropTargetId(null);
                        return;
                      }
                      const irmaosAlvo = tarefas
                        .filter((x) => x.parentId === t.parentId && x.depth === t.depth)
                        .sort((a, b) => a.outlineNumber.localeCompare(b.outlineNumber, 'pt', { numeric: true }));
                      const posAlvo = irmaosAlvo.findIndex((x) => x.id === t.id);
                      mover.mutate({ tarefaId: dragState.current.tarefaId, parentId: t.parentId, position: posAlvo });
                      dragState.current = null;
                      setDropTargetId(null);
                    }}
                    onDragEnd={() => { dragState.current = null; setDropTargetId(null); }}
                    className={cn(
                      'group',
                      dropTargetId === t.id && 'border-t-2 border-primary',
                      t.isMilestone ? 'bg-primary/5' : delayed ? 'bg-destructive/5' : t.isCritical ? 'bg-primary/5' : 'hover:bg-muted/20',
                    )}
                  >
                    <td className="w-8 px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selecionadas.has(t.id)}
                        onChange={(e) => {
                          setSelecionadas(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(t.id); else next.delete(t.id);
                            return next;
                          });
                        }}
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <span
                          className="cursor-grab select-none text-base leading-none text-muted-foreground/40 opacity-0 group-hover:opacity-100"
                          aria-hidden
                        >⠿</span>
                        {t.outlineNumber}
                      </div>
                    </td>
                    <td className="px-3 py-2" style={{ paddingLeft: `${(t.depth * 16) + 12}px` }}>
                      <button
                        onClick={() => setTarefaEditando(t)}
                        className="flex items-center gap-1.5 text-left hover:text-primary group"
                        title="Clique para editar"
                      >
                        {t.isMilestone && <span className="text-primary text-sm" aria-hidden>◆</span>}
                        <span className={cn('font-medium', t.isMilestone ? 'italic text-primary/90' : delayed ? 'text-destructive' : t.isCritical ? 'text-cyan-700 dark:text-cyan-400' : '')}>
                          {t.name}
                        </span>
                        {!t.isMilestone && delayed && (
                          <span className="ml-1 rounded px-1 py-px text-[9px] font-semibold bg-destructive/10 text-destructive">Atrasada</span>
                        )}
                        <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
                      </button>
                      {!t.isMilestone && !delayed && t.isCritical && <span className="ml-1 text-xs text-cyan-700/80 dark:text-cyan-400/70">crítico</span>}
                      {t.ticketId && (
                        <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-primary/10 px-1 py-0.5 text-xs text-primary ring-1 ring-inset ring-primary/20">
                          <Ticket className="h-3 w-3" />
                        </span>
                      )}
                      {t.deadline && (
                        <span className="ml-1 text-xs text-warning" title={`Prazo: ${epochToDateInput(t.deadline)}`}>⚑</span>
                      )}
                      {t.recurrence && t.recurrence !== 'NONE' && (
                        <span className="ml-1 inline-flex" title={`Recorrência: ${t.recurrence}`}>
                          <RefreshCw className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {t.isMilestone ? (
                        <span className="text-xs text-primary/80 italic">Marco</span>
                      ) : (() => {
                        const p = t.progress;
                        const label = p === 0 ? 'A fazer' : p === 100 ? 'Concluída' : 'Em andamento';
                        const cls = p === 0
                          ? 'bg-muted text-muted-foreground'
                          : p === 100
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400';
                        return (
                          <div>
                            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', cls)}>{label}</span>
                            {p > 0 && p < 100 && (
                              <div className="mt-1 h-1 w-full rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full bg-primary" style={{ width: `${p}%` }} />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      {t.isMilestone ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : t.assignee ? (
                        <div className="text-xs">
                          <div className="font-medium text-foreground truncate max-w-[120px]" title={t.assignee.displayName ?? t.assignee.name}>
                            {t.assignee.displayName ?? t.assignee.name}
                          </div>
                          {t.team && <div className="text-muted-foreground truncate max-w-[120px]">{t.team.name}</div>}
                        </div>
                      ) : t.team ? (
                        <span className="text-xs text-muted-foreground">{t.team.name}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {fmtData(t.startAt)}
                      {t.constraintType !== 'ASAP' && t.constraintType !== 'ALAP' && (
                        <span className="ml-1 rounded bg-warning/10 px-1 py-0.5 text-warning text-[10px]" title={`Restrição: ${t.constraintType}`}>
                          {t.constraintType}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {t.isMilestone
                        ? <span className="text-primary/70">◆ {fmtData(t.finishAt)}</span>
                        : fmtData(t.finishAt ? t.finishAt - 1 : null)}
                    </td>
                    <td className="px-3 py-2">
                      {t.predecessors.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {t.predecessors.map((dep) => {
                            const pred = tarefas.find((x) => x.id === dep.predecessorId);
                            return (
                              <span
                                key={dep.id}
                                className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground font-mono"
                                title={`${pred?.name ?? dep.predecessorId} · ${dep.kind}`}
                              >
                                {pred?.outlineNumber ?? '?'}{dep.kind !== 'FS' ? ` (${dep.kind})` : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-0.5">
                        <button aria-label="Mover para cima" disabled={idx <= 0 || mover.isPending} onClick={() => handleMover(t, 'cima')} className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button aria-label="Mover para baixo" disabled={idx >= irmaos.length - 1 || mover.isPending} onClick={() => handleMover(t, 'baixo')} className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        {!t.isMilestone && (
                          <button
                            aria-label="Adicionar subtarefa"
                            onClick={() => { setAdicionandoSubtarefaPai(t.id); setNomeSubtarefa(''); setDuracaoSubtarefaH('8'); }}
                            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-primary"
                            title="Adicionar subtarefa"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          aria-label="Vincular chamado"
                          onClick={() => { setVinculandoId(t.id); setBuscaChamado(''); setChamadoSelecionadoId(null); }}
                          className={cn('rounded p-0.5 hover:bg-muted', t.ticketId ? 'text-primary' : 'text-muted-foreground hover:text-foreground')}
                        >
                          <Ticket className="h-3.5 w-3.5" />
                        </button>
                        <button
                          aria-label="Editar / gerenciar dependências"
                          onClick={() => { setTarefaEditando(t); }}
                          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Link2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {adicionandoSubtarefaPai === t.id && (
                    <tr className="bg-muted/10 border-b border-primary/20">
                      <td colSpan={9} className="px-3 py-2">
                        <div
                          className="flex items-center gap-2"
                          style={{ paddingLeft: `${((t.depth + 1) * 16) + 12}px` }}
                        >
                          <input
                            autoFocus
                            value={nomeSubtarefa}
                            onChange={(e) => setNomeSubtarefa(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && nomeSubtarefa.trim()) criarSubtarefa.mutate();
                              if (e.key === 'Escape') { setAdicionandoSubtarefaPai(null); setNomeSubtarefa(''); }
                            }}
                            placeholder="Nome da subtarefa"
                            className="flex-1 min-w-32 rounded-lg border border-input/80 bg-background px-2 py-1.5 text-sm focus-visible:border-ring/40 focus-visible:outline-none focus-visible:shadow-glow"
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <label className="text-xs text-muted-foreground">h:</label>
                            <input
                              type="number" min="0.5" step="0.5"
                              value={duracaoSubtarefaH}
                              onChange={(e) => setDuracaoSubtarefaH(e.target.value)}
                              className="w-14 rounded-lg border border-input/80 bg-background px-2 py-1.5 text-sm focus-visible:border-ring/40 focus-visible:outline-none"
                            />
                          </div>
                          <button
                            onClick={() => criarSubtarefa.mutate()}
                            disabled={!nomeSubtarefa.trim() || criarSubtarefa.isPending}
                            className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 shrink-0"
                          >
                            {criarSubtarefa.isPending ? '…' : 'Adicionar'}
                          </button>
                          <button
                            onClick={() => { setAdicionandoSubtarefaPai(null); setNomeSubtarefa(''); }}
                            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal editar tarefa */}
      {tarefaEditando && (
        <ModalEditarTarefa
          projetoId={projetoId}
          tarefa={tarefaEditando}
          tarefas={tarefas}
          onFechar={() => setTarefaEditando(null)}
          onAtualizar={() => { onAtualizar(); setTarefaEditando(null); }}
        />
      )}

      {/* Modal vínculo chamado com autocomplete */}
      {vinculandoId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold">Vincular chamado à tarefa</h2>
              <button onClick={() => { setVinculandoId(null); setBuscaChamado(''); setChamadoSelecionadoId(null); }} className="rounded p-1 text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  autoFocus
                  value={buscaChamado}
                  onChange={(e) => { setBuscaChamado(e.target.value); setChamadoSelecionadoId(null); }}
                  placeholder="Digite o número ou título do chamado…"
                  className="h-9 w-full rounded-lg border border-input/80 bg-muted/40 pl-9 pr-3 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
                />
              </div>

              {chamadoSelecionadoId ? (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                  <span className="text-xs text-muted-foreground">Selecionado: </span>
                  <span className="font-medium text-primary">
                    {chamadosSugeridos.find((c) => c.id === chamadoSelecionadoId)?.number ?? chamadoSelecionadoId.slice(0, 8)}
                    {' — '}
                    {chamadosSugeridos.find((c) => c.id === chamadoSelecionadoId)?.subject}
                  </span>
                </div>
              ) : chamadosSugeridos.length > 0 ? (
                <ul className="flex flex-col gap-0.5 max-h-52 overflow-y-auto rounded-lg border border-border">
                  {chamadosSugeridos.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => { setChamadoSelecionadoId(c.id); }}
                        className="w-full flex items-start gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50 transition-colors"
                      >
                        <Ticket className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-muted-foreground mr-1">{c.number}</span>
                          <span className="font-medium truncate">{c.subject}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : buscaChamado.length >= 2 ? (
                <p className="text-xs text-muted-foreground py-2 text-center">Nenhum chamado encontrado.</p>
              ) : null}

              <div className="flex items-center justify-between border-t border-border pt-3">
                <button
                  onClick={() => vincular.mutate({ tarefaId: vinculandoId!, ticketId: null })}
                  disabled={vincular.isPending}
                  className="text-xs text-destructive hover:underline disabled:opacity-50"
                >
                  Desvincular
                </button>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setVinculandoId(null); setBuscaChamado(''); setChamadoSelecionadoId(null); }}>Cancelar</Button>
                  <Button variant="primary" size="sm" disabled={!chamadoSelecionadoId || vincular.isPending} loading={vincular.isPending}
                    onClick={() => vincular.mutate({ tarefaId: vinculandoId!, ticketId: chamadoSelecionadoId })}>
                    Vincular
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {selecionadas.size > 0 && (
        <BulkActionBar
          count={selecionadas.size}
          onClear={() => setSelecionadas(new Set())}
          onAplicar={(campo, valor) => {
            void Promise.all(
              [...selecionadas].map(id => {
                const body = campo === 'progress'
                  ? { progress: valor as number }
                  : { assigneeId: (valor as string) || null };
                return atualizarTarefa(projetoId, id, body);
              })
            ).then(() => {
              onAtualizar();
              setSelecionadas(new Set());
            });
          }}
          membros={membros}
        />
      )}
    </div>
  );
}

// ── BulkActionBar ─────────────────────────────────────────────────────────────

function BulkActionBar({
  count, onClear, onAplicar, membros,
}: {
  count: number;
  onClear: () => void;
  onAplicar: (campo: string, valor: string | number) => void;
  membros: Array<{ id: string; name: string; displayName?: string | null }>;
}) {
  const [modo, setModo] = useState<'idle' | 'progress' | 'assignee'>('idle');
  const [progresso, setProgresso] = useState(50);
  const [assigneeId, setAssigneeId] = useState('');

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 shadow-2xl">
      <span className="text-sm font-medium">{count} tarefa{count > 1 ? 's' : ''} selecionada{count > 1 ? 's' : ''}</span>
      {modo === 'idle' && (
        <>
          <button onClick={() => setModo('progress')} className="rounded-lg bg-muted px-3 py-1.5 text-xs hover:bg-muted/80">Alterar progresso</button>
          <button onClick={() => setModo('assignee')} className="rounded-lg bg-muted px-3 py-1.5 text-xs hover:bg-muted/80">Atribuir responsável</button>
          <button onClick={onClear} className="rounded-lg px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted">Cancelar</button>
        </>
      )}
      {modo === 'progress' && (
        <>
          <input type="number" min={0} max={100} value={progresso} onChange={e => setProgresso(Number(e.target.value))} className="w-16 rounded-lg border border-input/80 bg-muted/40 px-2 py-1 text-sm text-center tabular-nums" />
          <span className="text-xs text-muted-foreground">%</span>
          <button onClick={() => { onAplicar('progress', progresso); setModo('idle'); }} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">Aplicar</button>
          <button onClick={() => setModo('idle')} className="text-xs text-muted-foreground hover:underline">Voltar</button>
        </>
      )}
      {modo === 'assignee' && (
        <>
          <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} className="rounded-lg border border-input/80 bg-muted/40 px-2 py-1 text-sm">
            <option value="">Sem responsável</option>
            {membros.map(m => <option key={m.id} value={m.id}>{m.displayName ?? m.name}</option>)}
          </select>
          <button onClick={() => { onAplicar('assigneeId', assigneeId); setModo('idle'); }} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">Aplicar</button>
          <button onClick={() => setModo('idle')} className="text-xs text-muted-foreground hover:underline">Voltar</button>
        </>
      )}
    </div>
  );
}

// ── Board / Kanban ────────────────────────────────────────────────────────────

function BoardView({
  projetoId,
  tarefas,
  onMoverTarefa,
}: {
  projetoId: string;
  tarefas: TarefaDeProjeto[];
  onMoverTarefa: (tarefaId: string, secaoId: string, isDone: boolean) => void;
}) {
  const qc = useQueryClient();

  const secoesQuery = useQuery({
    queryKey: ['projetos', 'board-sections', projetoId],
    queryFn: () => listarSecoesDoProjeto(projetoId),
  });

  const criarSecaoMut = useMutation({
    mutationFn: (name: string) => criarSecao(projetoId, name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'board-sections', projetoId] }),
  });

  const renomearMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renomearSecao(projetoId, id, name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'board-sections', projetoId] }),
  });

  const excluirMut = useMutation({
    mutationFn: (id: string) => excluirSecao(projetoId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos', 'board-sections', projetoId] }),
  });

  const secoes = secoesQuery.data ?? [];

  const dragRef = useRef<{ tarefaId: string; secaoId: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const firstSecaoId = secoes[0]?.id ?? '';
  const tarefasPorSecao = useMemo(() => {
    const map = new Map<string, TarefaDeProjeto[]>();
    for (const s of secoes) map.set(s.id, []);
    for (const t of tarefas) {
      const target = (t.boardSectionId && map.has(t.boardSectionId)) ? t.boardSectionId : firstSecaoId;
      map.get(target)?.push(t);
    }
    return map;
  }, [secoes, tarefas, firstSecaoId]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [adicionando, setAdicionando] = useState(false);
  const [novaSecao, setNovaSecao] = useState('');

  const nowMin = Math.floor(Date.now() / 60_000);

  if (secoesQuery.isPending) {
    return <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>;
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 min-h-[400px]">
      {secoes.map((secao) => {
        const cards = tarefasPorSecao.get(secao.id) ?? [];
        const isTarget = dropTarget === secao.id;

        return (
          <div
            key={secao.id}
            className={cn(
              'group flex w-64 shrink-0 flex-col rounded-xl border bg-muted/30 transition-colors',
              isTarget ? 'border-primary/60 bg-primary/5' : 'border-border',
            )}
            onDragOver={(e) => { e.preventDefault(); setDropTarget(secao.id); }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDropTarget(null);
              if (dragRef.current && dragRef.current.secaoId !== secao.id) {
                onMoverTarefa(dragRef.current.tarefaId, secao.id, secao.isDone);
              }
              dragRef.current = null;
            }}
          >
            {/* Cabeçalho da coluna */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border">
              {editingId === secao.id ? (
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => {
                    if (editName.trim() && editName.trim() !== secao.name) {
                      renomearMut.mutate({ id: secao.id, name: editName.trim() });
                    }
                    setEditingId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') { setEditingId(null); }
                  }}
                  className="flex-1 rounded bg-background px-1 text-sm font-semibold outline-none border border-ring/40"
                />
              ) : (
                <button
                  className="flex-1 text-left text-sm font-semibold text-foreground hover:text-primary truncate"
                  onDoubleClick={() => { setEditingId(secao.id); setEditName(secao.name); }}
                  title="Clique duplo para renomear"
                >
                  {secao.name}
                </button>
              )}
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] text-muted-foreground tabular-nums">{cards.length}</span>
                {secoes.length > 1 && (
                  <button
                    onClick={() => {
                      if (!window.confirm(`Excluir a seção "${secao.name}"? As tarefas serão movidas para outra seção.`)) return;
                      excluirMut.mutate(secao.id);
                    }}
                    className="rounded p-0.5 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                    title="Excluir seção"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col gap-2 p-2 flex-1">
              {cards.map((t) => {
                const atrasada = t.progress < 100 && t.finishAt !== null && t.finishAt < nowMin;
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={() => { dragRef.current = { tarefaId: t.id, secaoId: secao.id }; }}
                    onDragEnd={() => { dragRef.current = null; }}
                    className={cn(
                      'rounded-lg border border-border bg-background p-3 cursor-grab active:cursor-grabbing select-none transition-shadow hover:shadow-sm',
                      atrasada && 'border-destructive/30 bg-destructive/5',
                    )}
                  >
                    <div className="flex items-start gap-1">
                      <p className="flex-1 text-xs font-medium leading-snug line-clamp-2">{t.name}</p>
                      {t.recurrence && t.recurrence !== 'NONE' && (
                        <span className="mt-0.5 inline-flex" title={`Recorrência: ${t.recurrence}`}>
                          <RefreshCw className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', t.progress === 100 ? 'bg-green-500' : t.isCritical ? 'bg-amber-500' : 'bg-primary')}
                          style={{ width: `${t.progress}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground tabular-nums">{t.progress}%</span>
                    </div>
                    {(() => {
                      const children = tarefas.filter(c => c.parentId === t.id);
                      const childCount = children.length;
                      if (childCount === 0) return null;
                      const doneCount = children.filter(c => c.progress === 100).length;
                      return (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <span>⊟</span>
                          <span className={doneCount === childCount ? 'text-green-600 font-medium' : ''}>
                            {doneCount}/{childCount}
                          </span>
                        </div>
                      );
                    })()}
                    {t.assignee && (
                      <div className="mt-2 flex items-center gap-1">
                        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 text-[8px] font-bold text-primary">
                          {(t.assignee.displayName ?? t.assignee.name).split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}
                        </div>
                        <span className="text-[10px] text-muted-foreground truncate">{t.assignee.displayName ?? t.assignee.name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Adicionar seção */}
      <div className="flex w-48 shrink-0 flex-col">
        {adicionando ? (
          <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2">
            <input
              autoFocus
              value={novaSecao}
              onChange={(e) => setNovaSecao(e.target.value)}
              placeholder="Nome da seção…"
              className="w-full rounded-lg border border-input/80 bg-background px-2 py-1 text-sm outline-none focus:border-ring/40"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && novaSecao.trim()) {
                  criarSecaoMut.mutate(novaSecao.trim());
                  setNovaSecao('');
                  setAdicionando(false);
                }
                if (e.key === 'Escape') { setAdicionando(false); setNovaSecao(''); }
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (novaSecao.trim()) {
                    criarSecaoMut.mutate(novaSecao.trim());
                    setNovaSecao('');
                    setAdicionando(false);
                  }
                }}
                className="rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                Criar
              </button>
              <button
                onClick={() => { setAdicionando(false); setNovaSecao(''); }}
                className="rounded-lg px-3 py-1 text-xs text-muted-foreground hover:bg-muted"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAdicionando(true)}
            className="flex items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nova seção
          </button>
        )}
      </div>
    </div>
  );
}

// ── Marcos ────────────────────────────────────────────────────────────────────

function MarcosView({
  projetoId, tarefas, onAtualizar,
}: { projetoId: string; tarefas: TarefaDeProjeto[]; onAtualizar: () => void }) {
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [marcoEditando, setMarcoEditando] = useState<TarefaDeProjeto | null>(null);

  const milestones = tarefas.filter((t) => t.isMilestone);
  const total = milestones.length;

  const criar = useMutation({
    mutationFn: () => criarTarefa(projetoId, { name: nome.trim(), isMilestone: true }),
    onSuccess: () => { setNome(''); setCriando(false); onAtualizar(); },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'Nenhum marco definido.' : `${total} marco(s)`}
        </p>
        {!criando && (
          <Button size="sm" variant="secondary" onClick={() => setCriando(true)} icon={<Plus className="h-3.5 w-3.5" />}>
            Novo marco
          </Button>
        )}
      </div>

      {criando && (
        <div className="rounded-lg border border-border bg-muted/10 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-medium">Novo marco</h3>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Nome</label>
            <input className={INPUT_CLS} autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Go-live fase 1" />
          </div>
          <p className="text-xs text-muted-foreground">
            Marcos são tarefas de duração zero — a data é calculada automaticamente pelo cronograma com base nas dependências que você definir na aba WBS.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setCriando(false); setNome(''); }}>Cancelar</Button>
            <Button size="sm" disabled={!nome.trim() || criar.isPending} loading={criar.isPending} onClick={() => criar.mutate()}>
              Criar marco
            </Button>
          </div>
        </div>
      )}

      {/* Marcos do cronograma (isMilestone tasks) */}
      {milestones.length > 0 && (
        <div className="flex flex-col gap-2">
          <ul className="flex flex-col gap-2">
            {milestones.map((m) => (
              <li key={m.id} className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3">
                <span className="text-primary text-base shrink-0 select-none" aria-hidden>◆</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    <span className="font-mono text-xs text-muted-foreground mr-1">{m.outlineNumber}</span>
                    {m.name}
                  </p>
                  {m.description && <p className="text-xs text-muted-foreground">{m.description}</p>}
                  {m.predecessors.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Após: {m.predecessors.map((d) => {
                        const pred = tarefas.find((t) => t.id === d.predecessorId);
                        return `${pred?.outlineNumber ?? '?'} (${d.kind})`;
                      }).join(', ')}
                    </p>
                  )}
                </div>
                {m.finishAt ? (
                  <span className="text-xs tabular-nums text-muted-foreground shrink-0">
                    {new Date(m.finishAt * 60 * 1000).toLocaleDateString('pt-BR')}
                  </span>
                ) : (
                  <span className="text-xs italic text-muted-foreground shrink-0">Sem data calculada</span>
                )}
                <Button variant="ghost" size="sm" onClick={() => setMarcoEditando(m)} aria-label="Editar marco">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal editar marco (milestone task) */}
      {marcoEditando && (
        <ModalEditarTarefa
          projetoId={projetoId}
          tarefa={marcoEditando}
          tarefas={tarefas}
          onFechar={() => setMarcoEditando(null)}
          onAtualizar={() => { onAtualizar(); setMarcoEditando(null); }}
        />
      )}
    </div>
  );
}

// ── Riscos ────────────────────────────────────────────────────────────────────

function RiscosView({
  projetoId, riscos, onAtualizar,
}: { projetoId: string; riscos: RiscoDeProjeto[]; onAtualizar: () => void }) {
  const [criando, setCriando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [severity, setSeverity] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'>('MEDIUM');
  const [mitigation, setMitigation] = useState('');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editMitigation, setEditMitigation] = useState('');

  const criar = useMutation({
    mutationFn: () => criarRisco(projetoId, { title: titulo.trim(), description: descricao.trim() || undefined, severity, mitigation: mitigation.trim() || undefined }),
    onSuccess: () => { setTitulo(''); setDescricao(''); setSeverity('MEDIUM'); setMitigation(''); setCriando(false); onAtualizar(); },
  });

  const atualizar = useMutation({
    mutationFn: ({ id }: { id: string }) => atualizarRisco(projetoId, id, { status: editStatus, mitigation: editMitigation.trim() || undefined }),
    onSuccess: () => { setEditandoId(null); onAtualizar(); },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {riscos.length === 0 ? 'Nenhum risco registrado.' : `${riscos.length} risco(s)`}
        </p>
        {!criando && (
          <Button size="sm" variant="secondary" onClick={() => setCriando(true)} icon={<Plus className="h-3.5 w-3.5" />}>
            Novo risco
          </Button>
        )}
      </div>

      {criando && (
        <div className="rounded-lg border border-border bg-muted/10 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-medium">Novo risco</h3>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Título</label>
            <input className={INPUT_CLS} autoFocus value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Descrição (opcional)</label>
            <textarea className={INPUT_CLS} rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Severidade</label>
              <select className={SELECT_CLS} value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
                {Object.entries(SEV_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Mitigação (opcional)</label>
            <textarea className={INPUT_CLS} rows={2} value={mitigation} onChange={(e) => setMitigation(e.target.value)} placeholder="Plano de mitigação" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setCriando(false); setTitulo(''); setDescricao(''); setMitigation(''); }}>Cancelar</Button>
            <Button size="sm" disabled={!titulo.trim() || criar.isPending} loading={criar.isPending} onClick={() => criar.mutate()}>
              Registrar risco
            </Button>
          </div>
        </div>
      )}

      {riscos.length > 0 && (
        <ul className="flex flex-col gap-2">
          {riscos.map((r) => (
            <li key={r.id} className="rounded-md border border-border bg-background px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{r.title}</p>
                    <span className={cn('text-xs font-semibold', SEV_COR[r.severity] ?? '')}>{SEV_PT[r.severity] ?? r.severity}</span>
                    <span className="text-xs text-muted-foreground">— {STATUS_RISCO_PT[r.status] ?? r.status}</span>
                  </div>
                  {r.description && <p className="mt-0.5 text-xs text-muted-foreground">{r.description}</p>}
                  {r.mitigation && <p className="mt-0.5 text-xs italic text-muted-foreground">Mitigação: {r.mitigation}</p>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEditandoId(r.id); setEditStatus(r.status); setEditMitigation(r.mitigation ?? ''); }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>

              {editandoId === r.id && (
                <div className="mt-3 border-t border-border pt-3 flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
                      <select className={SELECT_CLS} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                        {Object.entries(STATUS_RISCO_PT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Mitigação</label>
                    <textarea className={INPUT_CLS} rows={2} value={editMitigation} onChange={(e) => setEditMitigation(e.target.value)} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditandoId(null)}>Cancelar</Button>
                    <Button size="sm" disabled={atualizar.isPending} loading={atualizar.isPending} onClick={() => atualizar.mutate({ id: r.id })}>
                      Salvar
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Recursos ──────────────────────────────────────────────────────────────────

function RecursosView({
  projetoId, recursos, onAtualizar,
}: { projetoId: string; recursos: RecursoDeProjeto[]; onAtualizar: () => void }) {
  const [criando, setCriando] = useState(false);
  const [pessoa, setPessoa] = useState<PersonRef | null>(null);
  const [role, setRole] = useState('');
  const [allocationPct, setAllocationPct] = useState('100');

  const alocar = useMutation({
    mutationFn: () => alocarRecurso(projetoId, { userId: pessoa!.id, roleInProject: role.trim() || undefined, allocationPct: Number(allocationPct) }),
    onSuccess: () => { setPessoa(null); setRole(''); setAllocationPct('100'); setCriando(false); onAtualizar(); },
  });

  const desalocar = useMutation({
    mutationFn: (userId: string) => desalocarRecurso(projetoId, userId),
    onSuccess: onAtualizar,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {recursos.length === 0 ? 'Nenhum recurso alocado.' : `${recursos.length} recurso(s)`}
        </p>
        {!criando && (
          <Button size="sm" variant="secondary" onClick={() => setCriando(true)} icon={<Plus className="h-3.5 w-3.5" />}>
            Alocar recurso
          </Button>
        )}
      </div>

      {criando && (
        <div className="rounded-lg border border-border bg-muted/10 p-4 flex flex-col gap-3">
          <h3 className="text-sm font-medium">Alocar recurso</h3>
          <SeletorDePessoa rotulo="Pessoa" escolhida={pessoa} aoEscolher={setPessoa} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Papel no projeto (opcional)</label>
              <input className={INPUT_CLS} value={role} onChange={(e) => setRole(e.target.value)} placeholder="Ex: Analista de Negócio" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Alocação (%)</label>
              <input className={INPUT_CLS} type="number" min="1" max="100" value={allocationPct} onChange={(e) => setAllocationPct(e.target.value)} />
            </div>
          </div>
          {alocar.isError && <p className="text-xs text-destructive">Erro ao alocar — esse usuário já pode estar alocado neste projeto.</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setCriando(false); setPessoa(null); setRole(''); setAllocationPct('100'); }}>Cancelar</Button>
            <Button size="sm" disabled={!pessoa || alocar.isPending} loading={alocar.isPending} onClick={() => alocar.mutate()}>
              Alocar
            </Button>
          </div>
        </div>
      )}

      {recursos.length > 0 && (
        <ul className="flex flex-col gap-2">
          {recursos.map((r) => (
            <li key={r.id} className="flex items-center gap-3 rounded-md border border-border bg-background px-4 py-3">
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex-1 text-sm">
                <span className="font-mono text-xs">{r.userId.slice(0, 8)}…</span>
                {r.roleInProject && <span className="ml-2 text-muted-foreground">— {r.roleInProject}</span>}
              </div>
              <span className={cn('text-xs font-semibold tabular-nums', r.allocationPct > 100 ? 'text-destructive' : 'text-foreground')}>
                {r.allocationPct}%
              </span>
              <button
                onClick={() => { if (confirm('Remover recurso do projeto?')) desalocar.mutate(r.userId); }}
                disabled={desalocar.isPending}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-40"
                aria-label="Remover recurso"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── @mention textarea ─────────────────────────────────────────────────────────

function MentionTextarea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [mencaoAtiva, setMencaoAtiva] = useState(false);
  const [atPos, setAtPos] = useState(0);
  const [sugestoes, setSugestoes] = useState<MembroParaMencao[]>([]);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const buscar = useCallback(async (q: string) => {
    try {
      const res = await buscarMembros(q);
      setSugestoes(res);
      setHighlightIdx(0);
    } catch {
      setSugestoes([]);
    }
  }, []);

  function aoMudar(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    onChange(val);
    const pos = e.target.selectionStart ?? 0;
    // Encontra o @ mais próximo antes do cursor
    const antes = val.slice(0, pos);
    const match = /@([\wÀ-ɏ]*)$/.exec(antes);
    if (match) {
      setAtPos(pos - match[0].length);
      setMencaoAtiva(true);
      void buscar(match[1] ?? '');
    } else {
      setMencaoAtiva(false);
      setSugestoes([]);
    }
  }

  function inserirMencao(membro: MembroParaMencao) {
    const nome = membro.displayName ?? membro.name;
    const pos = ref.current?.selectionStart ?? 0;
    const antes = value.slice(0, atPos);
    const depois = value.slice(pos);
    const novo = `${antes}@${nome} ${depois}`;
    onChange(novo);
    setMencaoAtiva(false);
    setSugestoes([]);
    // Reposiciona cursor após o nome inserido
    setTimeout(() => {
      const nova = atPos + nome.length + 2;
      ref.current?.setSelectionRange(nova, nova);
      ref.current?.focus();
    }, 0);
  }

  function aoTeclar(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!mencaoAtiva || sugestoes.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % sugestoes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => (i - 1 + sugestoes.length) % sugestoes.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const sel = sugestoes[highlightIdx];
      if (sel) inserirMencao(sel);
    } else if (e.key === 'Escape') {
      setMencaoAtiva(false);
      setSugestoes([]);
    }
  }

  // Fecha ao clicar fora
  useEffect(() => {
    if (!mencaoAtiva) return;
    const fn = () => { setMencaoAtiva(false); setSugestoes([]); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [mencaoAtiva]);

  return (
    <div className="relative">
      <textarea
        ref={ref}
        className={cn(
          'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm resize-none',
          'transition-colors placeholder:text-muted-foreground',
          'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:shadow-glow focus-visible:outline-none',
        )}
        rows={rows}
        value={value}
        onChange={aoMudar}
        onKeyDown={aoTeclar}
        placeholder="Escreva aqui… use @Nome para mencionar"
      />
      {mencaoAtiva && sugestoes.length > 0 && (
        <ul
          className="absolute bottom-full left-0 z-50 mb-1 w-56 overflow-hidden rounded-lg border border-border bg-background shadow-elevation"
          onMouseDown={(e) => e.preventDefault()}
        >
          {sugestoes.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                onMouseDown={() => inserirMencao(m)}
                className={cn(
                  'w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent',
                  i === highlightIdx && 'bg-accent',
                )}
              >
                <span className="font-medium">{m.displayName ?? m.name}</span>
                {m.displayName && (
                  <span className="ml-1.5 text-[11px] text-muted-foreground">@{m.name}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  name: 'nome',
  description: 'descrição',
  assigneeId: 'responsável',
  progress: 'progresso',
  durationMinutes: 'duração',
  teamId: 'equipe',
  constraintType: 'restrição',
  deadline: 'prazo',
};

function AtividadeDescricao({ action, field, oldValue, newValue }: {
  action: string; field: string | null; oldValue: string | null; newValue: string | null;
}) {
  if (action === 'task.created') return <>criou a tarefa</>;
  if (action === 'task.assigned') {
    if (!newValue || newValue === 'null') return <>removeu o responsável</>;
    return <>atribuiu a tarefa</>;
  }
  if (action === 'task.progress') {
    return <>atualizou progresso para <strong>{newValue}%</strong></>;
  }
  if (action === 'task.updated' && field) {
    const label = FIELD_LABELS[field] ?? field;
    if (!newValue || newValue === 'null') return <>removeu {label}</>;
    if (!oldValue || oldValue === 'null') return <>definiu {label}</>;
    return <>alterou {label}</>;
  }
  return <>{action}</>;
}

function BurndownView({ data, isLoading }: {
  data?: { points: Array<{ date: number; remaining: number; ideal: number }>; total: number; done: number };
  isLoading: boolean;
}) {
  if (isLoading) return <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>;
  if (!data || data.points.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Dados insuficientes para o burndown. O projeto precisa de datas de início e término.
      </div>
    );
  }

  const { points, total, done } = data;
  const W = 600, H = 200, PAD = 40;
  const maxR = Math.max(...points.map((p) => Math.max(p.remaining, p.ideal)), 1);
  const toX = (i: number) => PAD + ((W - PAD * 2) * i) / Math.max(points.length - 1, 1);
  const toY = (v: number) => PAD + ((H - PAD * 2) * (1 - v / maxR));

  const idealPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.ideal).toFixed(1)}`).join(' ');
  const realPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.remaining).toFixed(1)}`).join(' ');

  const fmtDate = (ep: number) => new Date(ep * 60_000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-6 rounded-full bg-muted-foreground/40 inline-block" /> Ideal
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-6 rounded-full bg-primary inline-block" /> Real
        </span>
        <span className="text-muted-foreground">{done}/{total} tarefas concluídas</span>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-2xl" style={{ minWidth: 300 }}>
          {/* Grade horizontal */}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <g key={f}>
              <line x1={PAD} y1={toY(maxR * f)} x2={W - PAD} y2={toY(maxR * f)} stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} />
              <text x={PAD - 4} y={toY(maxR * f) + 4} textAnchor="end" fontSize={9} fill="currentColor" fillOpacity={0.4}>
                {Math.round(maxR * f)}
              </text>
            </g>
          ))}
          {/* Linha ideal */}
          <path d={idealPath} fill="none" stroke="currentColor" strokeOpacity={0.25} strokeWidth={1.5} strokeDasharray="4 3" />
          {/* Linha real */}
          <path d={realPath} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} strokeLinejoin="round" />
          {/* Rótulos do eixo X */}
          {[0, Math.floor((points.length - 1) / 2), points.length - 1].map((i) => (
            points[i] ? (
              <text key={i} x={toX(i)} y={H - PAD + 14} textAnchor="middle" fontSize={9} fill="currentColor" fillOpacity={0.4}>
                {fmtDate(points[i]!.date)}
              </text>
            ) : null
          ))}
        </svg>
      </div>
    </div>
  );
}

// ── Horas (time tracking) ─────────────────────────────────────────────────────

function HorasView({
  data,
  isLoading,
  onRegistrar,
  onExcluir,
  currentUserId,
}: {
  data?: ListaDeHoras;
  isLoading: boolean;
  onRegistrar: (minutes: number, description?: string) => void;
  onExcluir: (id: string) => void;
  currentUserId?: string | null;
}) {
  const [horas, setHoras] = useState('');
  const [desc, setDesc] = useState('');

  function fmtMin(m: number) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h${min > 0 ? ` ${min}min` : ''}` : `${min}min`;
  }

  function fmtDataLog(iso: string) {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  const submit = () => {
    const min = Math.round(parseFloat(horas.replace(',', '.')) * 60);
    if (!min || min < 1) return;
    onRegistrar(min, desc.trim() || undefined);
    setHoras('');
    setDesc('');
  };

  return (
    <div className="space-y-4">
      {/* Total */}
      {data && (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-3">
          <span className="text-xs text-muted-foreground">Total registrado:</span>
          <span className="text-sm font-semibold tabular-nums">{fmtMin(data.totalMinutes)}</span>
        </div>
      )}

      {/* Log form */}
      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-xs font-medium text-muted-foreground">Registrar horas</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="number"
              step="0.25"
              min="0.25"
              value={horas}
              onChange={(e) => setHoras(e.target.value)}
              placeholder="0"
              className="h-9 w-full rounded-lg border border-input/80 bg-muted/40 px-3 pr-8 text-sm tabular-nums focus-visible:outline-none focus-visible:border-ring/40"
            />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">h</span>
          </div>
          <input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Descrição (opcional)"
            className="h-9 flex-[3] rounded-lg border border-input/80 bg-muted/40 px-3 text-sm focus-visible:outline-none focus-visible:border-ring/40"
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          <button
            onClick={submit}
            disabled={!horas}
            className="h-9 rounded-lg bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          >
            Salvar
          </button>
        </div>
      </div>

      {/* Log list */}
      {isLoading ? (
        <div className="py-4 text-center text-sm text-muted-foreground">Carregando…</div>
      ) : (data?.logs.length ?? 0) === 0 ? (
        <div className="py-4 text-center text-sm text-muted-foreground">Nenhuma hora registrada.</div>
      ) : (
        <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
          {data!.logs.map((log: RegistroDeHoras) => (
            <div key={log.id} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted/20">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[9px] font-bold text-primary">
                {(log.user.displayName ?? log.user.name).split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}
              </div>
              <span className="text-xs text-muted-foreground flex-none">{log.user.displayName ?? log.user.name}</span>
              <span className="font-semibold tabular-nums text-primary">{fmtMin(log.minutes)}</span>
              {log.description && <span className="flex-1 text-xs text-muted-foreground truncate">{log.description}</span>}
              <span className="text-[10px] text-muted-foreground flex-none">{fmtDataLog(log.loggedAt)}</span>
              {log.user.id === currentUserId && (
                <button
                  onClick={() => onExcluir(log.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  title="Excluir"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Campos personalizados (EAV) ───────────────────────────────────────────────

function CamposView({
  data,
  isLoading,
  onCriar,
  onAtualizar,
  onExcluir,
}: {
  data?: CampoPersonalizado[];
  isLoading: boolean;
  onCriar: (name: string, value: string) => void;
  onAtualizar: (id: string, value: string) => void;
  onExcluir: (id: string) => void;
}) {
  const [newName, setNewName] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editing, setEditing] = useState<Record<string, string>>({});

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="py-4 text-center text-sm text-muted-foreground">Carregando…</div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="py-2 text-xs text-muted-foreground">Nenhum campo personalizado.</div>
      ) : (
        <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
          {data!.map((f) => (
            <div key={f.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="w-32 shrink-0 text-xs font-medium text-muted-foreground truncate">{f.fieldName}</span>
              {editing[f.id] !== undefined ? (
                <div className="flex flex-1 items-center gap-2">
                  <input
                    value={editing[f.id]}
                    onChange={(e) => setEditing((p) => ({ ...p, [f.id]: e.target.value }))}
                    className="flex-1 h-7 rounded-lg border border-input/80 bg-muted/40 px-2 text-xs focus-visible:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onAtualizar(f.id, editing[f.id] ?? '');
                        setEditing((p) => { const n = { ...p }; delete n[f.id]; return n; });
                      }
                      if (e.key === 'Escape') setEditing((p) => { const n = { ...p }; delete n[f.id]; return n; });
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      onAtualizar(f.id, editing[f.id] ?? '');
                      setEditing((p) => { const n = { ...p }; delete n[f.id]; return n; });
                    }}
                    className="text-[10px] text-primary hover:underline"
                  >
                    OK
                  </button>
                </div>
              ) : (
                <span
                  className="flex-1 text-sm cursor-pointer hover:text-primary"
                  onClick={() => setEditing((p) => ({ ...p, [f.id]: f.fieldValue }))}
                  title="Clique para editar"
                >
                  {f.fieldValue || <span className="text-muted-foreground italic">vazio</span>}
                </span>
              )}
              <button
                onClick={() => onExcluir(f.id)}
                className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                aria-label="Excluir campo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-xs font-medium text-muted-foreground">Adicionar campo</p>
        <div className="flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome do campo"
            className="flex-1 h-8 rounded-lg border border-input/80 bg-muted/40 px-3 text-xs focus-visible:outline-none focus-visible:border-ring/40"
          />
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="Valor"
            className="flex-1 h-8 rounded-lg border border-input/80 bg-muted/40 px-3 text-xs focus-visible:outline-none focus-visible:border-ring/40"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) {
                onCriar(newName.trim(), newValue.trim());
                setNewName('');
                setNewValue('');
              }
            }}
          />
          <button
            onClick={() => {
              if (newName.trim()) {
                onCriar(newName.trim(), newValue.trim());
                setNewName('');
                setNewValue('');
              }
            }}
            disabled={!newName.trim()}
            className="h-8 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          >
            + Campo
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Colaboradores de tarefa ───────────────────────────────────────────────────

function ColaboradoresSection({ projetoId, tarefaId }: { projetoId: string; tarefaId: string }) {
  const qc = useQueryClient();

  const { data: colaboradores = [] } = useQuery({
    queryKey: ['tarefa', 'colaboradores', tarefaId],
    queryFn: () => listarColaboradores(projetoId, tarefaId),
  });

  const adicionar = useMutation({
    mutationFn: () => adicionarColaborador(projetoId, tarefaId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tarefa', 'colaboradores', tarefaId] }),
  });

  const remover = useMutation({
    mutationFn: (userId: string) => removerColaborador(projetoId, tarefaId, userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tarefa', 'colaboradores', tarefaId] }),
  });

  function iniciais(u: { name: string; displayName: string | null }) {
    const n = u.displayName ?? u.name;
    return n.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground shrink-0">Colaboradores:</span>
      <div className="flex flex-wrap gap-1.5 items-center">
        {(colaboradores as ColaboradorDeTarefa[]).map((c) => (
          <button
            key={c.id}
            title={`${c.user.displayName ?? c.user.name} — clique para remover`}
            onClick={() => remover.mutate(c.userId)}
            className="group relative flex h-6 w-6 items-center justify-center rounded-full bg-primary/20 text-[10px] font-semibold text-primary ring-1 ring-primary/30 hover:bg-destructive/20 hover:text-destructive hover:ring-destructive/30 transition-colors"
          >
            {iniciais(c.user)}
          </button>
        ))}
        <button
          onClick={() => adicionar.mutate()}
          disabled={adicionar.isPending}
          title="Seguir esta tarefa"
          className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:text-primary transition-colors text-xs"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ── Mensagens de projeto ──────────────────────────────────────────────────────

function MensagensView({
  projetoId,
  data,
  isLoading,
}: {
  projetoId: string;
  data?: MensagemDeProjeto[];
  isLoading: boolean;
}) {
  const qc = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [novoAssunto, setNovoAssunto] = useState('');
  const [novoCorpo, setNovoCorpo] = useState('');
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');

  const criar = useMutation({
    mutationFn: () => criarMensagem(projetoId, { subject: novoAssunto.trim(), body: novoCorpo.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projetos', 'mensagens', projetoId] });
      setNovoAssunto('');
      setNovoCorpo('');
      setCriando(false);
    },
  });

  const responder = useMutation({
    mutationFn: (msgId: string) => responderMensagem(projetoId, msgId, replyBody.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projetos', 'mensagens', projetoId] });
      setReplyBody('');
    },
  });

  function fmtDataMsg(s: string) {
    return new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function nomeAutor(author: { name: string; displayName: string | null } | null) {
    if (!author) return 'Sistema';
    return author.displayName ?? author.name;
  }

  if (isLoading) return <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Botão nova mensagem */}
      <div className="flex justify-end">
        <button
          onClick={() => setCriando(true)}
          className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          + Nova mensagem
        </button>
      </div>

      {/* Formulário nova mensagem */}
      {criando && (
        <div className="rounded-xl border border-border bg-background p-4 space-y-3">
          <input
            autoFocus
            value={novoAssunto}
            onChange={(e) => setNovoAssunto(e.target.value)}
            placeholder="Assunto"
            className="w-full h-9 rounded-lg border border-input/80 bg-muted/40 px-3 text-sm focus-visible:outline-none focus-visible:border-ring/40 focus-visible:bg-background"
          />
          <textarea
            value={novoCorpo}
            onChange={(e) => setNovoCorpo(e.target.value)}
            placeholder="Escreva a mensagem…"
            rows={4}
            className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:border-ring/40 focus-visible:bg-background"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setCriando(false)} className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">
              Cancelar
            </button>
            <button
              onClick={() => criar.mutate()}
              disabled={!novoAssunto.trim() || !novoCorpo.trim() || criar.isPending}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {criar.isPending ? 'Enviando…' : 'Enviar'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de mensagens */}
      {!data?.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma mensagem ainda. Comece uma discussão!</p>
      ) : (
        data.map((msg) => (
          <div key={msg.id} className="rounded-xl border border-border bg-background overflow-hidden">
            {/* Header da mensagem */}
            <button
              onClick={() => setAbertaId(abertaId === msg.id ? null : msg.id)}
              className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{msg.subject}</span>
                  {msg.replies.length > 0 && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {msg.replies.length} resposta{msg.replies.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {nomeAutor(msg.author)} · {fmtDataMsg(msg.createdAt)}
                </div>
                {abertaId !== msg.id && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{msg.body}</p>
                )}
              </div>
              <span className="text-muted-foreground text-xs shrink-0">{abertaId === msg.id ? '▲' : '▼'}</span>
            </button>

            {/* Corpo expandido + respostas */}
            {abertaId === msg.id && (
              <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
                <p className="text-sm whitespace-pre-wrap">{msg.body}</p>

                {/* Respostas */}
                {msg.replies.map((r) => (
                  <div key={r.id} className="flex gap-2.5 pl-3 border-l-2 border-muted">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium">{nomeAutor(r.author)}</span>
                      <span className="text-[10px] text-muted-foreground ml-2">{fmtDataMsg(r.createdAt)}</span>
                      <p className="text-xs mt-0.5 whitespace-pre-wrap">{r.body}</p>
                    </div>
                  </div>
                ))}

                {/* Caixa de resposta */}
                <div className="flex gap-2 pt-1">
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Responder…"
                    rows={2}
                    className="flex-1 rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-xs resize-none focus-visible:outline-none focus-visible:border-ring/40 focus-visible:bg-background"
                  />
                  <button
                    onClick={() => responder.mutate(msg.id)}
                    disabled={!replyBody.trim() || responder.isPending}
                    className="self-end rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {responder.isPending ? '…' : 'Responder'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function CorpoComMencoes({ texto }: { texto: string }) {
  const partes = texto.split(/(@[\wÀ-ɏ]+(?:\s[\wÀ-ɏ]+)?)/g);
  return (
    <>
      {partes.map((parte, i) =>
        parte.startsWith('@') ? (
          <span key={i} className="font-medium text-primary">
            {parte}
          </span>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  );
}

// ── Calendário ────────────────────────────────────────────────────────────────

// ── Saúde do projeto ──────────────────────────────────────────────────────────

const SEVERITY_LABEL: Record<string, string> = {
  LOW: 'Baixo', MEDIUM: 'Médio', HIGH: 'Alto', CRITICAL: 'Crítico',
};
const STATUS_CONFIG = {
  ON_TRACK: { label: 'No prazo', cls: 'text-green-600 bg-green-50 dark:bg-green-900/20' },
  AT_RISK:  { label: 'Em risco', cls: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
  DELAYED:  { label: 'Atrasado', cls: 'text-destructive bg-destructive/10' },
};

function SaudeView({ data, isLoading }: { data?: SaudeDoProjeto; isLoading: boolean }) {
  if (isLoading) return <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>;
  if (!data) return null;

  const sc = STATUS_CONFIG[data.status];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Status geral */}
      <div className={cn('inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold', sc.cls)}>
        <span className={cn('h-2 w-2 rounded-full', data.status === 'ON_TRACK' ? 'bg-green-500' : data.status === 'AT_RISK' ? 'bg-amber-500' : 'bg-destructive')} />
        {sc.label}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Tarefas concluídas', value: `${data.tasksDone}/${data.tasksTotal}`, sub: `${data.progressPct}%`, cls: '' },
          { label: 'Em atraso', value: String(data.tasksOverdue), sub: 'tarefas', cls: data.tasksOverdue > 0 ? 'text-destructive' : '' },
          { label: 'Caminho crítico', value: String(data.criticalOpen), sub: 'abertas', cls: data.criticalOpen > 0 ? 'text-amber-600' : '' },
          { label: 'Risco principal', value: data.topRisk ? SEVERITY_LABEL[data.topRisk.severity] : '—', sub: data.topRisk?.title ?? 'Nenhum aberto', cls: data.topRisk && ['HIGH','CRITICAL'].includes(data.topRisk.severity) ? 'text-destructive' : '' },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-border bg-background p-4 space-y-1">
            <p className="text-xs text-muted-foreground">{kpi.label}</p>
            <p className={cn('text-2xl font-bold tabular-nums', kpi.cls)}>{kpi.value}</p>
            <p className="text-[10px] text-muted-foreground truncate">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Barra de progresso */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Progresso geral</span>
          <span className="tabular-nums font-medium">{data.progressPct}%</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full transition-all', data.progressPct === 100 ? 'bg-green-500' : data.status === 'DELAYED' ? 'bg-destructive' : 'bg-primary')}
            style={{ width: `${data.progressPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Calendário ────────────────────────────────────────────────────────────────

function CalendarioView({
  tasks,
  onAbrirTarefa,
}: {
  tasks: TarefaDeProjeto[];
  onAbrirTarefa: (t: TarefaDeProjeto) => void;
}) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth()); // 0-indexed

  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia = new Date(ano, mes + 1, 0);
  const diasNoMes = ultimoDia.getDate();
  // weekday of first day (0=Sun..6=Sat), shift to Mon-start
  const offsetSemana = (primeiroDia.getDay() + 6) % 7;

  const DIAS_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  function navMes(delta: number) {
    const d = new Date(ano, mes + delta, 1);
    setAno(d.getFullYear());
    setMes(d.getMonth());
  }

  // Build epoch minute ranges for the month
  const inicioMes = Math.floor(primeiroDia.getTime() / 60_000);
  const fimMes = Math.floor(new Date(ano, mes + 1, 0, 23, 59).getTime() / 60_000);

  // Tasks that overlap this month
  const tarefasDoMes = tasks.filter(
    (t) => !t.isMilestone && t.startAt !== null && t.finishAt !== null &&
      (t.startAt ?? 0) <= fimMes && (t.finishAt ?? 0) >= inicioMes,
  );

  function tarefasNoDia(dia: number): TarefaDeProjeto[] {
    const inicioEpoch = Math.floor(new Date(ano, mes, dia).getTime() / 60_000);
    const fimEpoch = Math.floor(new Date(ano, mes, dia, 23, 59).getTime() / 60_000);
    return tarefasDoMes.filter(
      (t) => (t.startAt ?? 0) <= fimEpoch && (t.finishAt ?? 0) > inicioEpoch,
    );
  }

  const totalCelulas = offsetSemana + diasNoMes;
  const linhas = Math.ceil(totalCelulas / 7);

  return (
    <div className="space-y-3">
      {/* Cabeçalho de navegação */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navMes(-1)}
          className="rounded p-1 hover:bg-accent text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {MESES[mes]} {ano}
        </span>
        <button
          type="button"
          onClick={() => navMes(1)}
          className="rounded p-1 hover:bg-accent text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Grade */}
      <div className="overflow-hidden rounded-lg border border-border">
        {/* Cabeçalho de dias da semana */}
        <div className="grid grid-cols-7 border-b border-border bg-muted/30">
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="py-1.5 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Células de dias */}
        <div className="grid grid-cols-7">
          {Array.from({ length: linhas * 7 }, (_, i) => {
            const dia = i - offsetSemana + 1;
            const valido = dia >= 1 && dia <= diasNoMes;
            const isHoje = valido && dia === hoje.getDate() && mes === hoje.getMonth() && ano === hoje.getFullYear();
            const tarefas = valido ? tarefasNoDia(dia) : [];
            return (
              <div
                key={i}
                className={cn(
                  'min-h-[80px] border-b border-r border-border p-1',
                  !valido && 'bg-muted/20',
                  i % 7 === 6 && 'border-r-0',
                  Math.floor(i / 7) === linhas - 1 && 'border-b-0',
                )}
              >
                {valido && (
                  <>
                    <div className={cn(
                      'mb-1 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium',
                      isHoje ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                    )}>
                      {dia}
                    </div>
                    <div className="space-y-0.5">
                      {tarefas.slice(0, 3).map((t) => {
                        const atrasada = (t.finishAt ?? 0) * 60_000 < Date.now() && t.progress < 100;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => onAbrirTarefa(t)}
                            className={cn(
                              'block w-full truncate rounded px-1 py-px text-[10px] text-left transition-opacity hover:opacity-80',
                              atrasada
                                ? 'bg-destructive/15 text-destructive'
                                : t.isCritical
                                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                                  : 'bg-primary/15 text-primary',
                            )}
                            title={t.name}
                          >
                            {t.name}
                          </button>
                        );
                      })}
                      {tarefas.length > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{tarefas.length - 3}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
